import React from 'react';
import { format } from 'date-fns';
import { RefreshCw, Droplets, Wind, Navigation } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import { useWeather, getConditionEmoji } from '../../components/WeatherWidget';

function getGreeting(date) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstName(user) {
  const raw = (user?.name || user?.username || '').trim();
  if (!raw) return '';
  return raw.split(/\s+/)[0];
}

function HeaderWeather() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MODULES.CONFIGURATION, 'write');
  const { weather, status } = useWeather();

  if (status === 'no-location') {
    if (!canManage) return null;
    return (
      <Link
        to="/dashboard/facility-location"
        className="inline-flex items-center gap-1 text-xs text-ga-primary hover:underline"
      >
        <Navigation className="h-3.5 w-3.5" /> Set ground location for weather
      </Link>
    );
  }

  if (status !== 'success' || !weather?.current) return null;

  const { current, forecast } = weather;
  const rain = forecast?.[0]?.chance_of_rain ?? 0;

  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ga-text-muted">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-lg leading-none">{getConditionEmoji(current.condition?.code)}</span>
        <span className="font-semibold text-ga-text-primary">{Math.round(current.temp_c)}°C</span>
        <span>{current.condition?.text}</span>
      </span>
      <span className="text-ga-text-subtle">feels {Math.round(current.feels_like_c)}°</span>
      <span className="inline-flex items-center gap-1">
        <Droplets className="h-3.5 w-3.5 text-blue-500" aria-hidden />
        {rain}% rain
      </span>
      <span className="inline-flex items-center gap-1">
        <Wind className="h-3.5 w-3.5 text-teal-500" aria-hidden />
        {Math.round(current.wind_kph)} km/h
      </span>
    </span>
  );
}

export default function HomeHeader({ facility, today, lastSyncedAt, onRefresh, summaryLoading, summaryError }) {
  const { user } = useAuth();
  const now = today instanceof Date ? today : new Date();
  const name = firstName(user);
  const greeting = name ? `${getGreeting(now)}, ${name}` : getGreeting(now);
  const facilityName = facility?.name;

  const syncedLabel = summaryLoading
    ? 'Syncing…'
    : summaryError
      ? "Couldn't refresh — showing last data"
      : lastSyncedAt
        ? `Synced ${Math.max(0, Math.floor((Date.now() - lastSyncedAt.getTime()) / 60000))} min ago`
        : 'Not synced yet';

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl md:text-3xl font-bold text-ga-text-primary truncate">{greeting}</h1>
        <p className="mt-1 text-sm text-ga-text-muted">
          {facilityName && <span className="font-medium text-ga-text-primary">{facilityName}</span>}
          {facilityName && <span className="px-2 text-ga-text-subtle">·</span>}
          <span>{format(now, 'EEEE, MMM d')}</span>
        </p>
        <div className="mt-2">
          <HeaderWeather />
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <span className={`text-xs ${summaryError ? 'text-ga-danger' : 'text-ga-text-subtle'}`}>{syncedLabel}</span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={summaryLoading}
          className="ga-btn-ghost inline-flex items-center gap-2 disabled:opacity-60"
          aria-label="Refresh dashboard data"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${summaryLoading ? 'animate-spin' : ''}`} aria-hidden />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>
    </div>
  );
}
